import type { Category, ProviderFileRef } from "fileParsing/types";

// Path type, pointing to value in JSON structure.
// Object keys are referenced by strings or numeric values,
// Arrays can be iterated through by ARRAY_INDICATORS,
// individual array values are referenced by the numeric index.
export const WILDCARD = null;
export type Path = (string | typeof WILDCARD | number)[];

export const genericPath = (e: string | number | null) => (typeof e === "number" ? WILDCARD : e);

export enum AlgoStep {
  ExtractData,
  Transform,
  Compile,
  Failed,
  NonExistent,
}

// Description of usage
export type TimeUsage = {
  format: "time";
  argument?: DateParserArguments;
  sample?: Date;
};
type URIUsage = { format: "uri" };
type URLUsage = { format: "url" };
type LongUsage = { format: "long" };
type LatUsage = { format: "lat" };

export type SupposedUsage = TimeUsage | URIUsage | URLUsage | LongUsage | LatUsage;

// Type Schemas
export type BooleanScalarType = {
  type: "boolean";
  supposedUsage?: SupposedUsage;
};
export type StringScalarType = {
  type: "string";
  supposedUsage?: SupposedUsage;
};
export type NumberScalarType = {
  type: "number";
  supposedUsage?: SupposedUsage;
};
export type ScalarType = BooleanScalarType | StringScalarType | NumberScalarType;
export type ArrayType = {
  type: "array";
  anyOf: TypeSchema[];
};
export type ObjectType = {
  type: "object";
  properties: { [key: string]: UnionType };
};
/*export type ObjectPattternType = {
  type: "object";
  patternProperties: { string: UnionType };
};*/
export type EmptyType = {
  type: "empty";
};
export type UnionType = TypeSchema[];
export type TypeSchema = ScalarType | ArrayType | ObjectType | EmptyType;

export const EMPTY_SCHEMA = [] as UnionType;

export type DataTemplate = { id: string } & (
  | { type: "text"; value: string }
  | { type: "data"; value: Path; label: string }
);

export enum DateLanguages {
  en,
  ja,
  fr,
  nl,
  de,
  pt,
  zh,
}
export type DateParserArguments =
  | { pattern: string }
  | { lang: DateLanguages }
  | { factor?: number; epoch?: number }
  | { iso: true };

export enum Visibility {
  visible,
  hidden,
  compressed,
}

export type DataPointTemplate = {
  path: Path; // genericPath
  schema?: UnionType;
  dataPointsCount: number;
  category?: Category;
  subcategory: DataTemplate[];
  visibility: Visibility;
  content: DataTemplate[];
  selectedTimestamps: { path: Path; arguments: DateParserArguments }[];
  selectedUris: Path[];
  selectedContacts: Path[];
  selectedLatitudes: Path[];
  selectedLongitudes: Path[];
  updated: boolean;
};

export type StructureType = {
  labelFileName: string;
  labelFilePath: string;
  labelParentFolderName: string;
  labelDate: string;
  data: unknown[];
};

export enum UserInteractionStatus {
  Automatic, // = Automatic wrangled
  Original, // = Loaded configuration
  Edited,
  Compiled, // = After user explicitly edited and resolved this instance
  Resolved, // = User resolved Automatic wrangled file without modifications
  Hidden,
}

export type WranglerData = {
  schema?: UnionType;
  structure: undefined | StructureType;
  byteData: Uint8Array | null | undefined;
  algoStep: AlgoStep; // Unnecessary? What about failed?
};

export type FileDataProgress = {
  readonly isNotEmpty: boolean;
  readonly foundDataPoints: boolean;
  readonly hasTimestamp: boolean;
  readonly guessedCategory: boolean;
  readonly didCompile: boolean;
};

export type WranglerItem = {
  id: number;
  name: string;
  exportId: number;
  schema: UnionType;
  templates: DataPointTemplate[];

  category: Category | undefined;
  userStatus: UserInteractionStatus;
  dataProgress: FileDataProgress;

  files: ProviderFileRef[];
};
